import { Group } from 'fabric';
import { assembleSpecs } from '../assemble';
import { framedBox } from '../kits/slotKit';
import type { BoxInterior } from '../kits/slotKit';
import { inchesToUnits } from '../kits/dotKit';
import type { PrimitiveSpec, TextSpec } from '../kits/specs';
import { translateSpecs, TEXT_BOX_HEIGHT } from '../kits/specs';
import { registerRecipe } from '../registry';
import { tagSmartObject } from '../types';
import type { ToolRecipe } from '../types';

/**
 * Answer box — a framed writing area with an empty, lined, or grid interior
 * (CATALOG F2, slotKit.framedBox), sized in PHYSICAL INCHES.
 *
 * THE 72-UNITS LAW (dotKit): the box is specified in inches and converted once
 * via inchesToUnits (72 workspace units = 1 inch; print exports at 300 dpi by
 * ×300/72). So widthIn 3 emits a frame exactly 216 units wide, which
 * rasterizes to exactly 900 px at 300 dpi — a teacher who asks for a 3×2 inch
 * box gets exactly that on print, not an eyeballed rectangle. Line spacing is
 * inches too, so ruled boxes match real paper. (Scaling the inserted group
 * trades the physical guarantee for a preview size — the geometry the recipe
 * emits is the honest one.)
 *
 * THE INTERIOR LAW (slotKit.framedBox): guide lines live strictly inside the
 * frame — first line one spacing in, none touching an edge — so ruling never
 * reads as a double-struck border. Lined = horizontals; grid adds verticals at
 * the same pitch.
 *
 * The optional label sits top-left above the box and renders exactly one
 * scalar param, so it is dblclick-editable on canvas for free (assemble.ts
 * stamps the {kind:'param'} engine tag). Clearing it drops the label entirely.
 *
 * Emits Rect + Line (+ optional FabricText) only, grouped and tagged
 * {tool,version,params} — plain fabric JSON, vector at any export dpi.
 */

// ---------------------------------------------------------------------------
// Defensive param coercion (the handwriting.ts pattern).
// ---------------------------------------------------------------------------

function num(v: unknown, fallback: number): number {
  const n = typeof v === 'string' && v.trim() !== '' ? Number(v) : (v as number);
  return typeof n === 'number' && Number.isFinite(n) ? n : fallback;
}

function str(v: unknown, fallback: string): string {
  return typeof v === 'string' && v.trim() !== '' ? v : fallback;
}

/** Unlike str(), an empty string is a MEANINGFUL value here (= no label). */
function rawStr(v: unknown, fallback: string): string {
  return typeof v === 'string' ? v : fallback;
}

const clamp = (v: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, v));

// ---------------------------------------------------------------------------
// Recipe
// ---------------------------------------------------------------------------

const INK = '#1e293b';
/** Neutral pencil-guide gray — lighter than the frame so ruling recedes. */
const GUIDE_COLOR = '#94a3b8';
const DEFAULT_FONT = 'Arial';

const INTERIORS = new Set<BoxInterior>(['empty', 'lined', 'grid']);

const answerBox: ToolRecipe = {
  id: 'answer-box',
  domain: 'ela',
  tags: ['answer-box', 'writing', 'response', 'lined-paper', 'grid', 'worksheet'],
  name: 'Answer box',
  category: 'practice',
  version: 1,
  description:
    'A framed writing area sized in inches — empty, lined, or grid inside. Ask for a 3×2 inch box and it prints at exactly that size.',
  // The inches ARE the contract — the editor must not preview-scale inserts
  // (widthIn 3 lands at exactly 216 units; see THE 72-UNITS LAW above).
  insertAtTrueScale: true,
  defaults: {
    label: 'Show your work',
    interior: 'lined',
    widthIn: 3,
    heightIn: 2,
    lineSpacing: 0.5,
    color: INK,
  },
  schema: [
    {
      key: 'label',
      label: 'Label (optional)',
      type: 'text',
      help: 'Small caption above the box, e.g. "Show your work". Leave empty for none.',
    },
    {
      key: 'interior',
      label: 'Inside',
      type: 'select',
      options: [
        { value: 'empty', label: 'Empty' },
        { value: 'lined', label: 'Lined' },
        { value: 'grid', label: 'Grid' },
      ],
    },
    {
      key: 'widthIn',
      label: 'Width (inches)',
      type: 'number',
      min: 0.5,
      max: 20,
      step: 0.25,
      help: 'Prints at exactly this size — 1 inch = 300 px at 300 dpi.',
    },
    {
      key: 'heightIn',
      label: 'Height (inches)',
      type: 'number',
      min: 0.5,
      max: 20,
      step: 0.25,
    },
    {
      key: 'lineSpacing',
      label: 'Line spacing (inches)',
      type: 'number',
      min: 0.15,
      max: 4,
      step: 0.05,
      section: 'Style',
      help: 'Gap between the ruled or grid lines.',
    },
    { key: 'color', label: 'Color', type: 'color', section: 'Style' },
  ],
  // Smart Tools modal exemplar (SM-A, 2026-07-23): caption + interior are the
  // teaching decision — hero them; the physical inches keep their own row and
  // Style stays one card.
  modal: { hero: ['label', 'interior'] },
  // Honest band values: ruling pitch is THE developmental knob for written
  // work — 1" primary ruling at K-2 stepping to ~0.3" college-ruled by 9-12
  // (all on the schema's 0.05 step). The box's physical size and interior
  // stay the teacher's content choice — bands never flip them.
  gradeBands: {
    K2: { lineSpacing: 1 },
    G35: { lineSpacing: 0.75 },
    G68: { lineSpacing: 0.5 },
    G912: { lineSpacing: 0.3 },
  },
  async generate(params) {
    const label = rawStr(params.label, '');
    const interiorRaw = str(params.interior, 'lined') as BoxInterior;
    const interior: BoxInterior = INTERIORS.has(interiorRaw) ? interiorRaw : 'lined';
    const widthIn = clamp(num(params.widthIn, 3), 0.25, 60);
    const heightIn = clamp(num(params.heightIn, 2), 0.25, 60);
    const lineSpacingIn = clamp(num(params.lineSpacing, 0.5), 0.1, 10);
    const color = str(params.color, INK);

    // The physical-units law: inches → units once, then the geometry is exact.
    const widthU = inchesToUnits(widthIn);
    const heightU = inchesToUnits(heightIn);
    const spacingU = inchesToUnits(lineSpacingIn);

    const box = framedBox({
      width: widthU,
      height: heightU,
      interior,
      spacing: spacingU,
      color,
      guideColor: GUIDE_COLOR,
    });

    const specs: PrimitiveSpec[] = [];

    // Optional caption top-left, ABOVE the frame (so it never overlaps the
    // writing area). Sized in inches too, floored so it stays legible.
    let boxY = 0;
    if (label !== '') {
      const labelSize = Math.max(inchesToUnits(0.22), heightU * 0.12);
      specs.push({
        kind: 'text',
        text: label,
        x: 0,
        y: 0,
        fontSize: labelSize,
        fontFamily: DEFAULT_FONT,
        fill: color,
        originX: 'left',
        originY: 'top',
        param: 'label',
      } satisfies TextSpec);
      boxY = labelSize * TEXT_BOX_HEIGHT + labelSize * 0.3;
    }

    // The frame keeps its exact widthU/heightU geometry; only its Y shifts to
    // clear the label. widthIn 3 ⇒ the frame rect is 216 units wide, always.
    specs.push(...translateSpecs(box.specs, 0, boxY));

    const group = new Group(assembleSpecs(specs));
    group.set({ left: 0, top: 0 });
    return tagSmartObject(group, answerBox, params);
  },
};

registerRecipe(answerBox);
